(() => {
  'use strict';

  const RUNTIME_KEY = '__lfmNewsletterFeedbackRuntime';
  const PENDING_PREFIX = 'lfm:newsletter-feedback:';
  const PENDING_TTL_MS = 30 * 60 * 1000;
  const DUPLICATE_WINDOW_MS = 500;
  const SUBMITTING_RESET_MS = 30000;

  function hasCustomerPosted(search) {
    try {
      return new URLSearchParams(String(search || '').replace(/^\?/, '')).get('customer_posted') === 'true';
    } catch (_error) {
      return false;
    }
  }

  function matchesFormFragment(hash, formId) {
    if (!formId) return false;
    const rawFragment = String(hash || '').replace(/^#/, '');
    if (!rawFragment) return false;
    try {
      return decodeURIComponent(rawFragment) === String(formId);
    } catch (_error) {
      return rawFragment === String(formId);
    }
  }

  function hasFreshPending(record, formId, campaign, now) {
    const timestamp = Number(record && record.at);
    const status = record && record.status;
    return Boolean(
      record &&
      record.formId === String(formId || '') &&
      record.campaign === String(campaign || '') &&
      (status === 'pending' || status === 'complete') &&
      Number.isFinite(timestamp) &&
      timestamp <= now &&
      timestamp > now - PENDING_TTL_MS
    );
  }

  function resolveFeedbackOutcome(options) {
    const formId = String(options.formId || '');
    const campaign = String(options.campaign || '');
    const pending = options.pending;
    const now = Number(options.now);
    if (!hasFreshPending(pending, formId, campaign, now)) return null;
    if (!matchesFormFragment(options.hash, formId)) return null;

    const serverState = String(options.serverState || 'none');
    const customerPosted = hasCustomerPosted(options.search);
    if (serverState === 'success') return customerPosted ? 'success' : null;
    if (serverState === 'error') return 'error';

    return customerPosted ? 'success' : null;
  }

  function shouldBlockDuplicate(lastTrustedSubmitAt, now, isTrusted) {
    const last = Number(lastTrustedSubmitAt);
    const current = Number(now);
    return Boolean(
      isTrusted &&
      Number.isFinite(last) &&
      Number.isFinite(current) &&
      current >= last &&
      current - last < DUPLICATE_WINDOW_MS
    );
  }

  function buildReturnTo(returnTo, currentHref, formId) {
    try {
      const current = new URL(currentHref);
      const target = new URL(returnTo, current.origin);
      if (target.origin !== current.origin || !formId) return String(returnTo || '');

      const merged = new URLSearchParams(target.search);
      merged.delete('customer_posted');
      const refreshedKeys = new Set();
      current.searchParams.forEach((value, key) => {
        if (key === 'customer_posted') return;
        if (!refreshedKeys.has(key)) {
          merged.delete(key);
          refreshedKeys.add(key);
        }
        merged.append(key, value);
      });
      merged.set('customer_posted', 'true');
      target.search = merged.toString();
      target.hash = String(formId);
      return target.pathname + target.search + target.hash;
    } catch (_error) {
      return String(returnTo || '');
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = {
      buildReturnTo,
      hasCustomerPosted,
      hasFreshPending,
      matchesFormFragment,
      resolveFeedbackOutcome,
      shouldBlockDuplicate,
    };
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window[RUNTIME_KEY]) return;

  const runtime = {
    forms: new WeakSet(),
    lastTrustedSubmit: new WeakMap(),
    submitTimers: new WeakMap(),
    impressionObserver: null,
    impressedForms: new WeakSet(),
  };
  window[RUNTIME_KEY] = runtime;

  function pendingKey(formId, campaign) {
    return PENDING_PREFIX + encodeURIComponent(campaign) + ':' + encodeURIComponent(formId);
  }

  function readPending(formId, campaign) {
    try {
      const raw = window.sessionStorage.getItem(pendingKey(formId, campaign));
      const record = raw ? JSON.parse(raw) : null;
      return record && typeof record === 'object' ? record : null;
    } catch (_error) {
      return null;
    }
  }

  function writePending(formId, campaign, status) {
    const record = {
      campaign: String(campaign || ''),
      formId: String(formId || ''),
      at: Date.now(),
      status: status || 'pending',
    };
    try {
      window.sessionStorage.setItem(pendingKey(formId, campaign), JSON.stringify(record));
    } catch (_error) {
      return null;
    }
    return record;
  }

  function completePending(formId, campaign, pending) {
    if (!pending) return;
    const completed = Object.assign({}, pending, { status: 'complete' });
    try {
      window.sessionStorage.setItem(pendingKey(formId, campaign), JSON.stringify(completed));
    } catch (_error) {
      return;
    }
  }

  function dispatchAnalytics(eventName, campaign, trigger) {
    document.dispatchEvent(new CustomEvent('lfnp:event', {
      detail: {
        event: eventName,
        campaign: campaign,
        format: 'embed',
        trigger: trigger,
        variant: 'A',
      },
    }));
  }

  function getFeedbackNodes(feedback) {
    return {
      success: feedback.querySelector('[data-lf-feedback-success]'),
      error: feedback.querySelector('[data-lf-feedback-error]'),
      submitting: feedback.querySelector('[data-lf-feedback-submitting]'),
    };
  }

  function hideResultNodes(feedback) {
    const nodes = getFeedbackNodes(feedback);
    if (nodes.success) nodes.success.hidden = true;
    if (nodes.error) nodes.error.hidden = true;
  }

  function clearSubmitting(form, feedback) {
    const timer = runtime.submitTimers.get(form);
    if (timer) window.clearTimeout(timer);
    runtime.submitTimers.delete(form);
    form.removeAttribute('aria-busy');
    const nodes = getFeedbackNodes(feedback);
    if (nodes.submitting) nodes.submitting.hidden = true;
  }

  function focusMessage(message) {
    if (!message || typeof message.focus !== 'function') return;
    try {
      message.focus({ preventScroll: true });
    } catch (_error) {
      message.focus();
    }
  }

  function revealOutcome(form, feedback, outcome, pending) {
    const nodes = getFeedbackNodes(feedback);
    hideResultNodes(feedback);
    clearSubmitting(form, feedback);

    const message = outcome === 'success' ? nodes.success : nodes.error;
    if (!message) return;
    message.hidden = false;

    const fields = form.querySelector('[data-lf-newsletter-fields]');
    if (fields) {
      fields.hidden = outcome === 'success';
      if (outcome === 'success') fields.setAttribute('aria-hidden', 'true');
      else fields.removeAttribute('aria-hidden');
    }

    const email = form.querySelector('input[type="email"]');
    if (email && message.id) email.setAttribute('aria-describedby', message.id);
    focusMessage(message);

    if (pending.status !== 'complete') {
      dispatchAnalytics(outcome === 'success' ? 'subscribe_success' : 'subscribe_error', feedback.dataset.lfFeedbackCampaign, 'result');
      completePending(form.id, feedback.dataset.lfFeedbackCampaign, pending);
    }
  }

  function updateReturnTarget(form) {
    const field = form.querySelector('input[name="return_to"]');
    if (!field) return;
    field.value = buildReturnTo(field.value, window.location.href, form.id);
  }

  function showSubmitting(form, feedback) {
    const nodes = getFeedbackNodes(feedback);
    hideResultNodes(feedback);
    if (nodes.submitting) nodes.submitting.hidden = false;
    form.setAttribute('aria-busy', 'true');
    const timer = runtime.submitTimers.get(form);
    if (timer) window.clearTimeout(timer);
    runtime.submitTimers.set(form, window.setTimeout(() => clearSubmitting(form, feedback), SUBMITTING_RESET_MS));
  }

  function handleSubmit(event, form, feedback) {
    const now = Date.now();
    const campaign = feedback.dataset.lfFeedbackCampaign || '';
    const lastTrustedSubmitAt = runtime.lastTrustedSubmit.get(form);
    if (shouldBlockDuplicate(lastTrustedSubmitAt, now, event.isTrusted)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (event.isTrusted) runtime.lastTrustedSubmit.set(form, now);
    updateReturnTarget(form);
    writePending(form.id, campaign, 'pending');
    showSubmitting(form, feedback);
    if (event.isTrusted) dispatchAnalytics('submit_attempt', campaign, 'manual');
  }

  function initializeFeedback(feedback) {
    const form = feedback.closest('form');
    if (!form || !form.id || runtime.forms.has(form)) return;
    runtime.forms.add(form);

    const campaign = feedback.dataset.lfFeedbackCampaign || '';
    const serverState = feedback.dataset.lfFeedbackServerState || 'none';
    const resultNodes = getFeedbackNodes(feedback);
    if (resultNodes.success) resultNodes.success.hidden = true;
    if (resultNodes.error) resultNodes.error.hidden = true;
    if (resultNodes.submitting) resultNodes.submitting.hidden = true;
    updateReturnTarget(form);

    const pending = readPending(form.id, campaign);
    const outcome = resolveFeedbackOutcome({
      serverState: serverState,
      pending: pending,
      formId: form.id,
      campaign: campaign,
      now: Date.now(),
      hash: window.location.hash,
      search: window.location.search,
    });
    if (outcome) revealOutcome(form, feedback, outcome, pending);

    form.addEventListener('submit', (event) => handleSubmit(event, form, feedback), true);
    form.addEventListener('focusin', () => {
      if (form.hasAttribute('aria-busy')) clearSubmitting(form, feedback);
    });
    form.addEventListener('input', () => {
      if (form.hasAttribute('aria-busy')) clearSubmitting(form, feedback);
    });
    form.addEventListener('change', () => {
      if (form.hasAttribute('aria-busy')) clearSubmitting(form, feedback);
    });
  }

  function isVisible(element) {
    if (!element || !element.isConnected || element.hidden) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    return true;
  }

  function isNewsletterPopupOpen() {
    return Array.from(document.querySelectorAll('lfm-newsletter-popup dialog[open]')).some(isVisible);
  }

  function isInViewport(element) {
    if (!isVisible(element)) return false;
    const rect = element.getBoundingClientRect();
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const area = rect.width * rect.height;
    return area > 0 && (width * height) / area >= 0.25;
  }

  function recordImpression(form, feedback) {
    if (runtime.impressedForms.has(form) || isNewsletterPopupOpen() || !isInViewport(form)) return;
    const campaign = feedback.dataset.lfFeedbackCampaign || '';
    if (campaign !== 'newsletter-home' && campaign !== 'newsletter-footer') return;
    runtime.impressedForms.add(form);
    dispatchAnalytics('impression', campaign, 'manual');
  }

  function checkVisibleForms() {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('[data-lf-newsletter-feedback]').forEach((feedback) => {
        const form = feedback.closest('form');
        if (form) recordImpression(form, feedback);
      });
    });
  }

  function initializeImpressions() {
    const feedbackNodes = Array.from(document.querySelectorAll('[data-lf-newsletter-feedback]'));
    const tracked = feedbackNodes
      .map((feedback) => ({ feedback: feedback, form: feedback.closest('form') }))
      .filter((item) => item.form && ['newsletter-home', 'newsletter-footer'].includes(item.feedback.dataset.lfFeedbackCampaign || ''));
    if (!tracked.length) return;

    if ('IntersectionObserver' in window) {
      runtime.impressionObserver = new IntersectionObserver((entries) => {
        if (isNewsletterPopupOpen()) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.25) return;
          const item = tracked.find((candidate) => candidate.form === entry.target);
          if (item) recordImpression(item.form, item.feedback);
        });
      }, { threshold: [0.25] });
      tracked.forEach((item) => runtime.impressionObserver.observe(item.form));
    } else {
      window.addEventListener('scroll', checkVisibleForms, { passive: true });
      window.addEventListener('resize', checkVisibleForms, { passive: true });
    }

    document.querySelectorAll('lfm-newsletter-popup dialog').forEach((dialog) => {
      dialog.addEventListener('close', checkVisibleForms);
    });
    document.addEventListener('lfnp:event', checkVisibleForms);
    window.addEventListener('pageshow', checkVisibleForms);
    checkVisibleForms();
  }

  document.querySelectorAll('[data-lf-newsletter-feedback]').forEach(initializeFeedback);
  initializeImpressions();
})();
